import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import { actesTrash, actesDossierLifecycle, actesTrashDemande, type ActesTrash, type ActesReference } from '@/integrations/actesApi';
import { countryOfRef, actesEn } from '@/lib/actesCountry';

/**
 * LA CORBEILLE — what was deleted, and the way back.
 *
 * Nothing in Actes is ever destroyed by a delete: the row is marked and lands
 * here. A demande that went down WITH its dossier is deliberately not listed
 * on its own — restoring the dossier brings its demandes with it, and showing
 * both would invite restoring half a dossier.
 *
 * Restoring stays with super admins, like deleting does.
 */
export function TrashPanel({ lang, reference }: { lang: Lang; reference: ActesReference }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const [err, setErr] = useState('');

  const q = useQuery<ActesTrash>({
    queryKey: ['actes', 'trash'],
    queryFn: ({ signal }) => actesTrash(signal),
    staleTime: 30_000,
  });

  const when = (iso: string | null) =>
    (iso ? new Date(iso).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
      day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
    }) : '—');

  /* ── RESTAURER SANS ATTENDRE ──
     The row used to sit there spinning until a round trip came back, and on a
     bulk restore that is one spinner after another. A restore practically
     never fails — it flips a column back — so the row leaves AT ONCE and the
     request follows. If the server does refuse, the row comes back and says
     why: an optimistic list that cannot undo itself is a lie, not a speed-up. */
  const [gone, setGone] = useState<Set<string>>(new Set());
  const restore = async (kind: 'dossier' | 'acte', id: string) => {
    setErr('');
    setGone((g) => new Set(g).add(id));
    try {
      if (kind === 'dossier') await actesDossierLifecycle(id, 'restore');
      else await actesTrashDemande(id, true);
      await qc.invalidateQueries({ queryKey: ['actes'] });
      /* Only once the list has actually been refetched without it. */
      setGone((g) => { const n = new Set(g); n.delete(id); return n; });
    } catch (e) {
      setGone((g) => { const n = new Set(g); n.delete(id); return n; });
      setErr(friendlyError(e, lang));
    }
  };
  /* Restoring everything on screen, one request each but a single wait. */
  const restoreAll = async (kind: 'dossier' | 'acte', ids: string[]) => {
    if (!ids.length) return;
    setErr('');
    setGone((g) => { const n = new Set(g); ids.forEach((i) => n.add(i)); return n; });
    const failed: string[] = [];
    for (const id of ids) {
      try {
        if (kind === 'dossier') await actesDossierLifecycle(id, 'restore');
        else await actesTrashDemande(id, true);
      } catch { failed.push(id); }
    }
    await qc.invalidateQueries({ queryKey: ['actes'] });
    setGone((g) => { const n = new Set(g); ids.forEach((i) => n.delete(i)); return n; });
    if (failed.length) setErr(T(`${failed.length} n'ont pas pu être restaurés.`, `${failed.length} could not be restored.`));
  };

  /* ── AU-DESSUS DE LA PORTE ──
     These two sat BELOW the super-admin gate below, so a viewer who is not a
     super admin rendered two hooks fewer than one who is — React #300, the
     same crash that took Handover down twice this session. Every hook in this
     component belongs above the first return, without exception. */
  const [byWho, setByWho] = useState('');
  const [onDay, setOnDay] = useState('');

  /* France: super admins. Portugal: its own team opens its own bin. */
  if (countryOfRef(reference) === 'PT' ? !reference.me.isStaff : !reference.me.isSuper) {
    return (
      <div className="estate">
        <div className="ico">🔒</div>
        <h3>{T('Réservé aux super admins', 'Super admins only')}</h3>
        <p>{T('Restaurer un élément supprimé est une action d’administration.',
              'Restoring a deleted item is an administration action.')}</p>
      </div>
    );
  }

  const allDossiers = q.data?.dossiers ?? [];
  const allActes = q.data?.actes ?? [];

  /* WHO THREW IT OUT, AND WHEN. A bin of two hundred rows is searched by one
     of those two — "what did I delete this morning", "what did Julien remove
     last week" — and neither was answerable. Both lists share the filter, so
     the answer is the whole bin's answer. */
  const dayOf = (iso: string | null) => (iso || '').slice(0, 10);
  const people = [...new Set([...allDossiers, ...allActes]
    .map((r) => r.deleted_by_name || '').filter(Boolean))].sort();
  const days = [...new Set([...allDossiers, ...allActes]
    .map((r) => dayOf(r.deleted_at)).filter(Boolean))].sort().reverse();
  const keep = <R extends { deleted_by_name: string | null; deleted_at: string | null; id: string }>(rows: R[]) =>
    rows.filter((r) => {
      if (gone.has(r.id)) return false;
      if (byWho && (r.deleted_by_name || '') !== byWho) return false;
      if (onDay && dayOf(r.deleted_at) !== onDay) return false;
      return true;
    });
  const dossiers = keep(allDossiers);
  const actes = keep(allActes);
  const nAll = allDossiers.length + allActes.length;
  const nShown = dossiers.length + actes.length;
  const dayLabel = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(
    lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', year: '2-digit' });

  return (
    <section className="actes-panel v7">
      <div className="phead">
        <h2>{T('Corbeille', 'Trash')}</h2>
        <div className="right">
          <span className="mono sub">
            {nShown}{nShown !== nAll ? ` / ${nAll}` : ''} {T('éléments', 'items')}
          </span>
        </div>
      </div>

      {/* ── QUI, ET QUAND ──
          A bin of two hundred rows is searched by one of two questions —
          "what did I delete this morning", "what did Julien remove last
          week" — and neither could be asked. Both lists share the filter, so
          the count above is the whole bin's answer. */}
      {nAll > 0 && (
        <div className="toolbar">
          {/* The two questions, said the same way. They were a `.actes-field
              inline` each — a stacked label above a raw <select> — so one sat
              higher than the other and neither matched anything else on the
              screen. One pill per filter, label inside, same height, aligned. */}
          <label className="hint">
            {T('Supprimé par', 'Deleted by')}
            <select className="sel" value={byWho} onChange={(e) => setByWho(e.target.value)}>
              <option value="">{T('tout le monde', 'everyone')}</option>
              {people.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </label>
          <span className="vr" />
          <label className="hint">
            {T('Le jour', 'On')}
            <select className="sel" value={onDay} onChange={(e) => setOnDay(e.target.value)}>
              <option value="">{T('n’importe quand', 'any day')}</option>
              {days.map((d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
            </select>
          </label>
          {(byWho || onDay) && (
            <button type="button" className="btn btn-s" onClick={() => { setByWho(''); setOnDay(''); }}>
              {T('Tout voir', 'Show all')}
            </button>
          )}
          {nShown > 1 && (byWho || onDay) && (
            <button type="button" className="btn btn-sm btn-ghost acv-restall"
              onClick={() => void restoreAll('dossier', dossiers.map((d) => d.id)).then(() => restoreAll('acte', actes.map((a) => a.id)))}>
              <KBIcon name="reset" size={13} /> {T(`Tout restaurer (${nShown})`, `Restore all (${nShown})`)}
            </button>
          )}
        </div>
      )}

      {err && <p className="muted-note ni-err">⚠ {err}</p>}
      {q.isLoading && <p className="muted-note">{T('Chargement…', 'Loading…')}</p>}

      <div className="card">
        <div className="card-h">
          <h3>{T('Dossiers supprimés', 'Deleted dossiers')}</h3>
          <span className="c mono">{dossiers.length}</span>
        </div>
        {dossiers.length === 0
          ? <p className="muted-note">{T('Aucun dossier supprimé.', 'No deleted dossier.')}</p>
          : (
            /* UNE LIGNE PAR CHOSE.
               Le nom au-dessus, la date et l'auteur en dessous, en 11 px :
               deux lignes empilées pour trois mots, et une page qu'on ne
               peut pas parcourir du regard. Chaque suppression tient
               maintenant sur une seule ligne, en colonnes alignées. */
            <ul className="actes-trash">
              {dossiers.map((d) => (
                <li key={d.id} className="actes-trash-r">
                  <strong className="actes-trash-n" title={d.name}>{d.name}</strong>
                  <span className="actes-trash-w">{d.deleted_by_name || '—'}</span>
                  <span className="actes-trash-d">{when(d.deleted_at)}</span>
                  <button className="btn btn-sm btn-ghost actes-trash-b"
                    onClick={() => void restore('dossier', d.id)}>
                    <KBIcon name="reset" size={13} /> {T('Restaurer', 'Restore')}
                  </button>
                </li>
              ))}
            </ul>
          )}
      </div>

      <div className="card">
        <div className="card-h">
          <h3>{T('Demandes supprimées', 'Deleted requests')}</h3>
          <span className="c mono">{actes.length}</span>
        </div>
        <div className="empty" style={{ borderBottom: '1px solid var(--line-2)', padding: '0.6875rem 1rem' }}>
          {T('Les demandes parties avec leur dossier ne sont pas listées ici : restaurer le dossier les ramène.',
             'Requests that went down with their dossier are not listed here: restoring the dossier brings them back.')}
        </div>
        {actes.length === 0
          ? <p className="muted-note">{T('Aucune demande supprimée.', 'No deleted request.')}</p>
          : (
            <ul className="actes-trash wide">
              {actes.map((a) => (
                <li key={a.id} className="actes-trash-r">
                  <strong className="actes-trash-n" title={a.person_concerned}>{a.person_concerned}</strong>
                  <span className="actes-trash-x" title={a.dossier_name ?? ''}>
                    {a.dossier_name || <em>{T('sans dossier', 'no dossier')}</em>}
                  </span>
                  <span className="actes-trash-w">{a.deleted_by_name || '—'}</span>
                  <span className="actes-trash-d">{when(a.deleted_at)}</span>
                  <button className="btn btn-sm btn-ghost actes-trash-b"
                    onClick={() => void restore('acte', a.id)}>
                    <KBIcon name="reset" size={13} /> {T('Restaurer', 'Restore')}
                  </button>
                </li>
              ))}
            </ul>
          )}
      </div>
    </section>
  );
}
